/**
 * 商品オプション管理システム
 * 
 * product_options メタフィールドから必須・任意オプションを読み込み、
 * ユーザーの選択に応じて価格と納期を動的に計算します。
 * 
 * @class ProductOptionsManager
 * @author 株式会社丸庄
 * @version 1.0.0
 * @date 2025-11-12
 */

class ProductOptionsManager {
  /**
   * コンストラクタ
   * @param {Object} productOptions - product_options メタフィールドデータ
   * @param {DynamicPriceCalculator} priceCalculator - 価格計算システム
   */
  constructor(productOptions, priceCalculator) {
    
    if (!productOptions || (Array.isArray(productOptions) && productOptions.length === 0)) {
      console.warn('[ProductOptionsManager] No product options found');
      this.hasOptions = false;
      return;
    }
    
    this.hasOptions = true;
    
    // 配列形式のproductOptions（Metaobject形式）
    if (Array.isArray(productOptions)) {
      // 商品に紐付けられているオプションは全て表示対象
      // （公開状況は商品への紐付けで管理するため、isPublishedチェックは不要）
      // choices が文字列の場合はパース（二重エンコード対応）
      this.allOptions = productOptions
        .map(opt => {
          if (typeof opt.choices === 'string') {
            try {
              opt.choices = JSON.parse(opt.choices);
              // 二重エンコードされている場合、もう一度パース
              if (typeof opt.choices === 'string') {
                opt.choices = JSON.parse(opt.choices);
              }
            } catch (e) {
              console.warn('[ProductOptionsManager] Failed to parse choices for option:', opt.id, e);
              opt.choices = [];
            }
          }
          return opt;
        });
      
      // 商品個別の並び順設定があるかチェック
      const customOrder = window.productData?.option_display_order;
      let useCustomOrder = false;

      // デバッグ: option_display_orderの値を詳細出力
      if (customOrder) {
      }

      // useGlobalがfalse（ブール値または文字列）の場合に個別設定を使用
      if (customOrder && typeof customOrder === 'object' && (customOrder.useGlobal === false || customOrder.useGlobal === 'false')) {
        // 商品個別設定を使用
        useCustomOrder = true;

        // 個別設定に基づいてソート
        const orderMap = {};
        if (customOrder.required && Array.isArray(customOrder.required)) {
          customOrder.required.forEach((id, index) => {
            orderMap[id] = index;
          });
        }
        const requiredCount = customOrder.required?.length || 0;
        if (customOrder.optional && Array.isArray(customOrder.optional)) {
          customOrder.optional.forEach((id, index) => {
            orderMap[id] = requiredCount + index;
          });
        }


        // IDマッチング確認
        let matchedCount = 0;
        const unmatchedIds = [];
        this.allOptions.forEach(opt => {
          if (orderMap[opt.id] !== undefined) {
            matchedCount++;
          } else {
            unmatchedIds.push(opt.id);
          }
        });
        if (unmatchedIds.length > 0) {
          console.warn('[ProductOptionsManager] Unmatched option IDs:', unmatchedIds);
        }

        this.allOptions.sort((a, b) => {
          const orderA = orderMap[a.id] ?? 9999;
          const orderB = orderMap[b.id] ?? 9999;
          return orderA - orderB;
        });
      } else {
        // 共通設定（display_order）でソート
        if (customOrder) {
        }
        this.allOptions.sort((a, b) => {
          const orderA = parseInt(a.display_order) || 9999;
          const orderB = parseInt(b.display_order) || 9999;
          return orderA - orderB;
        });
      }

      
      // requiredプロパティに基づいて分類
      this.requiredOptions = this.allOptions.filter(opt => opt.required === true);
      this.optionalOptions = this.allOptions.filter(opt => opt.required !== true);
      
    } else {
      // 旧形式: required/optionalプロパティがある場合
      this.requiredOptions = productOptions.required || [];
      this.optionalOptions = productOptions.optional || [];
      this.allOptions = [...this.requiredOptions, ...this.optionalOptions];
    }
    
    this.priceCalculator = priceCalculator;
    
    // 現在の選択状態を初期化（必須/任意を分けて管理）
    // 既存の選択内容がある場合は保持
    if (!this.selectedOptions) {
      this.selectedOptions = {
        required: {},  // { optionId: selectedChoice }
        optional: {}   // { optionId: selectedChoice }
      };
    } else {
    }
    
    // オプションデータをIDでインデックス化
    this.optionsById = {};
    this.allOptions.forEach(opt => {
      this.optionsById[opt.id] = opt;
    });
    
    
    // 条件付き表示のルール（印刷色数に応じたオプション表示制御）
    // Metaobjectから読み込んだルールがあれば使用、なければハードコードのフォールバック
    this.conditionalVisibilityRules = this.loadVisibilityRules();
    
    // UIの初期化
    this.initializeUI();
  }
  
  /**
   * オプション表示条件ルールを読み込む
   * Metaobjectからのルールを優先、なければハードコードのフォールバック
   * @returns {Array} ルール配列
   */
  loadVisibilityRules() {
    // 色タイプ選択は印刷色数モーダル内で行うため、「カラーの選択」オプションは常に非表示
    // 2026-01-27: color-type-selector.js と price-calculator-modals.js で色選択UIを統合
    const colorSelectionHideRule = {
      triggerPattern: /.*/,  // 全ての印刷色数にマッチ
      showOptions: [],
      hideOptions: ['カラーの選択【表面】', 'カラーの選択【裏面】', 'カラーの選択（表面）', 'カラーの選択（裏面）', 'カラーの選択'],
      filterChoices: []
    };

    // Metaobjectから読み込んだルールがあれば使用（カラー選択非表示ルールを追加）
    if (window.optionVisibilityRules && window.optionVisibilityRules.length > 0) {
      const rules = window.optionVisibilityRules.map(rule => ({
        triggerPattern: new RegExp(rule.triggerPattern),
        showOptions: rule.showOptions || [],
        hideOptions: rule.hideOptions || [],
        filterChoices: rule.filterChoices || []
      }));
      // カラー選択非表示ルールを先頭に追加
      return [colorSelectionHideRule, ...rules];
    }

    // フォールバック: カラー選択は常に非表示（他のルールは不要になったため削除）
    return [colorSelectionHideRule];
  }
  
  /**
   * 印刷色数変更時にオプション表示を更新
   * @param {string} printColor - 選択された印刷色数
   */
  updateColorOptionVisibility(printColor) {
    if (!printColor) return;

    // conditionalVisibilityRulesが配列でない場合は早期リターン
    if (!Array.isArray(this.conditionalVisibilityRules)) return;

    // マッチするルールを探す
    let matchedRule = null;
    for (const rule of this.conditionalVisibilityRules) {
      if (rule.triggerPattern.test(printColor)) {
        matchedRule = rule;
        break;
      }
    }
    
    if (!matchedRule) {
      return;
    }
    
    
    // オプションボタンの表示/非表示を更新
    this.allOptions.forEach(option => {
      const optionLabel = option.label || option.name || '';
      const buttonContainer = document.querySelector(`[data-option-id="${option.id}"]`)?.closest('.nav-item');
      
      if (!buttonContainer) return;
      
      // 非表示リストに含まれる場合
      if (matchedRule.hideOptions.some(pattern => optionLabel.includes(pattern))) {
        buttonContainer.style.display = 'none';
        // 非表示時は選択をクリア
        delete this.selectedOptions.required[option.id];
        delete this.selectedOptions.optional[option.id];
      }
      // 表示リストに含まれる場合
      else if (matchedRule.showOptions.some(pattern => optionLabel.includes(pattern))) {
        buttonContainer.style.display = '';
        // フィルターが指定されている場合は選択肢を制限
        if (matchedRule.filterChoices && matchedRule.filterChoices.length > 0) {
          option._filteredChoices = matchedRule.filterChoices;
        } else {
          option._filteredChoices = null;
        }
      }
    });
    
    // 価格を再計算
    this.recalculatePrice();
    
    // GroupedOptionsManagerのサイドバーボタンも更新（件数表示を印刷色数に連動）
    if (window.groupedOptionsManager && typeof window.groupedOptionsManager.createSidebarButtons === 'function') {
      window.groupedOptionsManager.createSidebarButtons();
    }
  }
  
  /**
   * UIの初期化
   */
  initializeUI() {
    if (!this.hasOptions) return;
    
    
    // ★ URLパラメータからオプションの初期値を設定
    this.initializeFromUrlParams();
    
    // オプションボタンを生成してサイドバーに追加
    this.renderOptionButtons();
    
    // オプションボタンにクリックイベントを設定
    document.querySelectorAll('.option-button').forEach(button => {
      const optionId = button.dataset.optionId;
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.showOptionModal(optionId);
      });
    });
    
  }
  
  /**
   * URLパラメータからオプションの初期値を設定
   * 再注文時にオプションを自動選択する
   */
  initializeFromUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // 再注文モードかどうかを確認
    const isReorder = urlParams.get('reorder') === 'true';
    if (isReorder) {
    }
    
    // 各オプションに対してURLパラメータから値を設定
    this.allOptions.forEach(option => {
      const optionLabel = option.label || option.name;
      const optionId = option.id;
      
      // URLパラメータからこのオプションの値を探す
      // ラベル名でもオプションIDでも検索
      let paramValue = urlParams.get(optionLabel) || 
                       urlParams.get(encodeURIComponent(optionLabel)) ||
                       urlParams.get(optionId);
      
      if (paramValue) {
        
        // 注: URLSearchParams.get()は既にデコード済みなので、追加のデコードは不要
        // encodeURIComponentで取得した場合のみ、そのパラメータ名がエンコードされている
        
        // 選択肢から一致するものを探す（完全一致を優先）
        const choices = option.choices || [];
        // まず完全一致を探す
        let matchingChoice = choices.find(choice => {
          const normalized = this.normalizeChoice(choice);
          const choiceLabel = normalized.label || '';
          return typeof choiceLabel === 'string' && choiceLabel === paramValue;
        });
        // 完全一致がない場合のみ、部分一致を試みる（ただしより厳格に）
        if (!matchingChoice) {
          matchingChoice = choices.find(choice => {
            const normalized = this.normalizeChoice(choice);
            const choiceLabel = normalized.label || '';
            // choiceLabelが文字列で、paramValueが十分長く（3文字以上）、かつchoiceLabelがparamValueで始まるか終わる場合のみ
            return typeof choiceLabel === 'string' && paramValue.length >= 3 && 
                   (choiceLabel.startsWith(paramValue) || choiceLabel.endsWith(paramValue));
          });
        }
        
        if (matchingChoice) {
          // 選択肢を正規化（price_modifier, lead_time_modifierを確実に含める）
          const normalizedChoice = this.normalizeChoice(matchingChoice);
          
          // 選択状態を更新
          if (option.required === true) {
            this.selectedOptions.required[optionId] = normalizedChoice;
          } else {
            this.selectedOptions.optional[optionId] = normalizedChoice;
          }
        } else {
        }
      }
    });
    
    // 選択後に価格と納期を再計算
    if (Object.keys(this.selectedOptions.required).length > 0 || 
        Object.keys(this.selectedOptions.optional).length > 0) {
      this.recalculatePrice();
    }
  }
  
  /**
   * オプションボタンをサイドバーに描画
   */
  renderOptionButtons() {
    const container = document.getElementById('product-options-container');
    if (!container) {
      console.error('[ProductOptionsManager] Options container not found');
      return;
    }
    
    // 統合オプションモードの場合はスキップ
    if (window.useGroupedOptions) {
      return;
    }
    
    // 重複を排除するため、optionsByIdから取得
    const uniqueOptions = Object.values(this.optionsById);
    const uniqueRequired = uniqueOptions.filter(opt => opt.required === true);
    const uniqueOptional = uniqueOptions.filter(opt => opt.required !== true);
    
    let html = '';
    
    // 必須オプション
    if (uniqueRequired.length > 0) {
      html += `
        <div class="nav-section">
          <div class="nav-section-header" style="background: #fff3cd; padding: 8px 12px; margin: -8px -12px 8px -12px; border-left: 4px solid #ffc107; font-weight: bold; color: #856404;">
            ⚠️ 必須オプション（${uniqueRequired.length}件）
          </div>
      `;
      uniqueRequired.forEach(option => {
        html += this.generateOptionButtonHTML(option, true);
      });
      html += '</div>';
    }
    
    // 任意オプション
    if (uniqueOptional.length > 0) {
      html += `
        <div class="nav-section">
          <div class="nav-section-header" style="background: #e7f3ff; padding: 8px 12px; margin: -8px -12px 8px -12px; border-left: 4px solid #2196F3; font-weight: bold; color: #0c5da5;">
            ℹ️ 任意オプション（${uniqueOptional.length}件）
          </div>
      `;
      uniqueOptional.forEach(option => {
        html += this.generateOptionButtonHTML(option, false);
      });
      html += '</div>';
    }
    
    container.innerHTML = html;
  }
  
  /**
   * オプションボタンのHTMLを生成
   * @param {Object} option - オプションデータ
   * @param {boolean} isRequired - 必須かどうか
   * @returns {string} HTML文字列
   */
  generateOptionButtonHTML(option, isRequired) {
    const currentSelection = isRequired 
      ? this.selectedOptions.required[option.id]
      : this.selectedOptions.optional[option.id];
    
    const displayText = currentSelection 
      ? currentSelection.label 
      : (isRequired ? '選択してください' : '選択なし');
    
    const requiredBadge = isRequired 
      ? '<span style="display: inline-block; background: #dc3545; color: white; padding: 2px 6px; border-radius: 3px; font-size: 0.75em; margin-left: 4px; font-weight: bold;">必須</span>' 
      : '';
    
    const buttonStyle = isRequired && !currentSelection
      ? 'border: 2px solid #ffc107; background: #fff9e6;'
      : '';
    
    return `
      <div class="nav-item">
        <div class="nav-content">
          <div class="nav-label">${option.label}${requiredBadge}</div>
          <button class="nav-button option-button" data-option-id="${option.id}" style="${buttonStyle}">
            <span class="nav-value" id="option-${option.id}-display">${displayText}</span>
            <span class="nav-arrow">⚙️</span>
          </button>
        </div>
      </div>
    `;
  }
  
  /**
   * オプション選択モーダルを表示
   * @param {string} optionId - オプションID
   */
  showOptionModal(optionId) {
    const option = this.optionsById[optionId];
    if (!option) {
      console.error('[ProductOptionsManager] Option not found:', optionId);
      return;
    }
    
    
    // モーダルHTML生成
    const modalHTML = this.generateModalHTML(option);
    
    // 既存のモーダルを削除
    const existingModal = document.getElementById('option-modal');
    if (existingModal) {
      existingModal.remove();
    }
    
    // モーダルを挿入
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    // モーダルイベントの設定
    this.setupModalEvents(option);
  }
  
  /**
   * 説明文の画像パスを修正
   * @param {string} description - 説明文HTML
   * @returns {string} 修正されたHTML
   */
  fixImagePaths(description) {
    if (!description) return '';
    
    // 相対パスの画像を絶対パスに変換
    // /images/... -> https://maruiro.jp/images/...
    return description.replace(
      /src="\/images\//g,
      'src="https://maruiro.jp/images/'
    );
  }

  /**
   * モーダルHTMLを生成
   * @param {Object} option - オプションデータ
   * @returns {string} HTML文字列
   */
  generateModalHTML(option) {
    const isRequired = this.allOptions.find(opt => opt.id === option.id && opt.required);
    
    // 現在の選択状態を取得（required/optionalの両方をチェック）
    const currentSelection = this.selectedOptions.required[option.id] || this.selectedOptions.optional[option.id];
    
    // choices または options プロパティを取得
    let choices = option.choices || option.options || [];
    
    // choicesがまだ文字列の場合はパース
    if (typeof choices === 'string') {
      try {
        choices = JSON.parse(choices);
        // 二重エンコード対応
        if (typeof choices === 'string') {
          choices = JSON.parse(choices);
        }
      } catch (e) {
        console.error('[ProductOptionsManager] Failed to parse choices in modal:', e);
        choices = [];
      }
    }
    
    // choicesが配列でない場合は空配列にする
    if (!Array.isArray(choices)) {
      console.warn('[ProductOptionsManager] choices is not an array:', choices);
      choices = [];
    }
    
    // フィルターが設定されている場合、選択肢をフィルタリング（C/M/Kのみ表示等）
    if (option._filteredChoices && option._filteredChoices.length > 0) {
      const filterPatterns = option._filteredChoices;
      const originalCount = choices.length;
      choices = choices.filter(choice => {
        const normalizedChoice = this.normalizeChoice(choice);
        const label = normalizedChoice.label || '';
        // フィルターパターンのいずれかにマッチするか、CMYK/フルカラーを除外
        const matchesFilter = filterPatterns.some(pattern => 
          label.includes(pattern) || label.startsWith(pattern)
        );
        // 「CMYK」「フルカラー」は1色印刷では除外
        const isCMYK = label.includes('CMYK') || label.includes('フルカラー');
        return matchesFilter && !isCMYK;
      });
    }
    
    
    // サイズ指定オプションかどうかを判定
    const isSizeOption = option.label && option.label.includes('サイズの指定');
    const hasCustomSizeChoice = isSizeOption && choices.some(c => 
      c && c.label && (c.label.includes('変型') || c.label.includes('変形'))
    );
    
    let choicesHTML = '';
    
    if (option.type === 'select' || option.type === 'radio') {
      choicesHTML = choices
        .filter(choice => choice !== null && choice !== undefined) // nullやundefinedを除外
        .map((choice, index) => {
          // 選択肢を正規化（文字列/オブジェクト両対応）
          const normalizedChoice = this.normalizeChoice(choice);
          const label = normalizedChoice.label || `選択肢 ${index + 1}`;
          const value = normalizedChoice.value || `value-${index}`;
          
          const isChecked = currentSelection && currentSelection.value === value;
          const priceText = this.formatPriceModifier(normalizedChoice.price_modifier);
          const leadTimeText = this.formatLeadTimeModifier(normalizedChoice.lead_time_modifier);
          const additionalInfo = [priceText, leadTimeText].filter(t => t).join(' / ');
          
          // この選択肢が「変型サイズ」かどうか
          const isCustomSize = isSizeOption && label && (label.includes('変型') || label.includes('変形'));
          
          return `
            <div class="option-choice">
              <input 
                type="radio" 
                name="option-choice-${option.id}" 
                value="${value}" 
                id="choice-${option.id}-${value}"
                data-is-custom-size="${isCustomSize}"
                ${isChecked ? 'checked' : ''}
              >
              <label for="choice-${option.id}-${value}">
                <div class="choice-label">${label}</div>
                ${additionalInfo ? `<div class="choice-info">${additionalInfo}</div>` : ''}
              </label>
            </div>
          `;
        }).join('');
    } else if (option.type === 'checkbox') {
      choicesHTML = choices
        .filter(choice => choice !== null && choice !== undefined) // nullやundefinedを除外
        .map((choice, index) => {
          // 選択肢を正規化（文字列/オブジェクト両対応）
          const normalizedChoice = this.normalizeChoice(choice);
          const label = normalizedChoice.label || `選択肢 ${index + 1}`;
          const value = normalizedChoice.value || `value-${index}`;
          
          const isChecked = currentSelection && currentSelection.value === value;
          const priceText = this.formatPriceModifier(normalizedChoice.price_modifier);
          const leadTimeText = this.formatLeadTimeModifier(normalizedChoice.lead_time_modifier);
          const additionalInfo = [priceText, leadTimeText].filter(t => t).join(' / ');
          
          return `
            <div class="option-choice">
              <input 
                type="checkbox" 
                name="option-choice-${option.id}" 
                value="${value}" 
                id="choice-${option.id}-${value}"
                ${isChecked ? 'checked' : ''}
              >
              <label for="choice-${option.id}-${value}">
                <div class="choice-label">${label}</div>
                ${additionalInfo ? `<div class="choice-info">${additionalInfo}</div>` : ''}
              </label>
            </div>
          `;
        }).join('');
    }
    
    // 説明文の画像パスを修正
    let description = this.fixImagePaths(option.description);
    
    // 改行コードをHTMLに変換（grouped-options-managerと同じロジック）
    description = this.processNewlines(description);
    
    // サイズ入力フォームのHTML（変型サイズ選択時のみ表示）
    const sizeInputHTML = hasCustomSizeChoice ? `
      <div class="size-input-container" id="size-input-${option.id}" style="display: none; margin-top: 15px; padding: 15px; background: #f5f5f5; border-radius: 5px;">
        <div style="margin-bottom: 10px; font-weight: 600;">入力欄 →</div>
        <div style="display: flex; align-items: center; gap: 10px;">
          <input 
            type="number" 
            id="size-width-${option.id}" 
            placeholder="幅"
            min="1"
            style="width: 100px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
          />
          <span>mm × </span>
          <input 
            type="number" 
            id="size-height-${option.id}" 
            placeholder="高さ"
            min="1"
            style="width: 100px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
          />
          <span>mm</span>
        </div>
      </div>
    ` : '';
    
    return `
      <div class="config-dialog-overlay" id="option-modal">
        <div class="config-dialog option-modal">
          <div class="config-dialog-header">
            <h3>${option.label}</h3>
            <button class="close-btn" id="option-modal-close">&times;</button>
          </div>
          <div class="config-dialog-content">
            ${description ? `<div class="option-description">${description}</div>` : ''}
            <div class="option-choices">
              ${choicesHTML}
            </div>
            ${sizeInputHTML}
          </div>
          <div class="config-dialog-footer">
            <button class="cancel-btn" id="option-modal-cancel">キャンセル</button>
            <button class="select-btn" id="option-modal-apply">選択</button>
          </div>
        </div>
      </div>
    `;
  }
  
  /**
   * 複雑なHTML（floatレイアウト等）かどうかを判定
   * @param {string} text - テキスト
   * @returns {boolean} 複雑なHTMLの場合true
   */
  isComplexHtml(text) {
    if (!text) return false;
    
    // floatスタイルを含む場合
    if (/float\s*:\s*(left|right)/i.test(text)) return true;
    
    // display:block + ul/li の組み合わせ
    if (/<(ul|li)[^>]*style[^>]*(display\s*:\s*block)/i.test(text)) return true;
    
    // 複数のdivネストがある場合
    const divMatches = text.match(/<div/gi);
    if (divMatches && divMatches.length >= 2) return true;
    
    // テーブルがある場合
    if (/<table/i.test(text)) return true;
    
    return false;
  }

  /**
   * 改行コードをHTML表示用に適切に処理する
   * CSVから読み込んだデータは改行コードが含まれているが、HTMLでは無視されるため変換が必要
   * @param {string} text - テキスト
   * @returns {string} 処理されたテキスト
   */
  processNewlines(text) {
    if (!text) return '';
    
    // 既に<br>タグが含まれている場合は変換済みとみなしてスキップ
    if (text.includes('<br>') || text.includes('<br/>') || text.includes('<br />')) {
      return text;
    }
    
    // 複雑なHTMLの場合は改行変換をスキップ（タグ間の改行が<br>になるとレイアウトが崩れる）
    if (this.isComplexHtml(text)) {
      return text;
    }
    
    // HTMLタグが含まれているかチェック
    const hasHtmlTags = /<[^>]+>/.test(text);
    
    if (hasHtmlTags) {
      // HTMLタグが含まれる場合：インデント用の改行を削除
      let processed = text;
      
      // 1. タグ直後の改行+空白を削除（>\s*\r\n\s* → >）
      processed = processed.replace(/>\s*\r?\n\s*/g, '>');
      
      // 2. タグ直前の改行+空白を削除（\s*\r\n\s*< → <）
      processed = processed.replace(/\s*\r?\n\s*</g, '<');
      
      // 3. 残った改行（コンテンツ部分）を<br>に変換
      processed = processed.replace(/\r?\n/g, '<br>');
      
      return processed;
    } else {
      // プレーンテキストの場合：すべての改行を<br>に変換
      return text.replace(/\r?\n/g, '<br>');
    }
  }
  
  /**
   * 選択肢文字列から価格と日数情報をパース
   * CSVの元データ形式: "ワード（基本料金: 0円 追加日数: 1日）"
   * または従量制: "ミシン入れ1本（基本料金: 1500円 + 1枚単価 @0.8円 × 数量, 追加日数: 1日）"
   * @param {string} choiceString - 選択肢の文字列
   * @returns {Object} {label, price_modifier, lead_time_modifier}
   */
  parseChoiceString(choiceString) {
    if (!choiceString || typeof choiceString !== 'string') {
      return { label: String(choiceString || ''), price_modifier: 0, lead_time_modifier: 0 };
    }
    
    // ラベル部分を抽出（括弧の前の部分）
    let label = choiceString;
    let priceModifier = 0;
    let leadTimeModifier = 0;
    
    // 括弧内の情報をパース
    const match = choiceString.match(/^(.+?)（(.+)）$/);
    if (match) {
      label = match[1].trim();
      const infoStr = match[2];
      
      // 基本料金を抽出
      const basePriceMatch = infoStr.match(/基本料金:\s*(\d+)円/);
      if (basePriceMatch) {
        priceModifier = parseInt(basePriceMatch[1], 10) || 0;
      }
      
      // 追加日数を先にパース（従量制でも使用するため）
      const daysMatch = infoStr.match(/追加日数:\s*(\d+)日/);
      if (daysMatch) {
        leadTimeModifier = parseInt(daysMatch[1], 10) || 0;
      }
      
      // 従量制（基本料金 + 1枚単価）の場合
      const unitPriceMatch = infoStr.match(/1枚単価\s*@([\d.]+)円/);
      if (unitPriceMatch && priceModifier > 0) {
        // 従量制の場合はオブジェクト形式で返す
        const unitPrice = parseFloat(unitPriceMatch[1]) || 0;
        return {
          label,
          price_modifier: {
            type: 'base_plus_unit',
            value: priceModifier,
            unit_price: unitPrice
          },
          lead_time_modifier: leadTimeModifier
        };
      }
    }
    
    return {
      label,
      price_modifier: priceModifier,
      lead_time_modifier: leadTimeModifier
    };
  }
  
  /**
   * 選択肢を正規化（文字列/オブジェクト両対応）
   * @param {string|Object} choice - 選択肢（文字列またはオブジェクト）
   * @returns {Object} 正規化された選択肢オブジェクト
   */
  normalizeChoice(choice) {
    if (typeof choice === 'string') {
      // 文字列の場合はパースして変換
      const parsed = this.parseChoiceString(choice);
      return {
        label: parsed.label,
        value: choice, // 元の文字列をvalueとして保持（一意性のため）
        price_modifier: parsed.price_modifier,
        lead_time_modifier: parsed.lead_time_modifier
      };
    }
    
    // オブジェクトの場合
    if (typeof choice === 'object' && choice !== null) {
      // labelがない場合、またはlabelに「基本料金」が含まれる場合はパースを試行
      const label = choice.label || choice.value || '';
      if (label.includes('基本料金:') || label.includes('追加日数:')) {
        const parsed = this.parseChoiceString(label);
        return {
          label: parsed.label,
          value: choice.value || label,
          price_modifier: choice.price_modifier || parsed.price_modifier,
          lead_time_modifier: choice.lead_time_modifier || parsed.lead_time_modifier
        };
      }
      
      return {
        label: choice.label || choice.value || '',
        value: choice.value || choice.label || '',
        price_modifier: choice.price_modifier || 0,
        lead_time_modifier: choice.lead_time_modifier || 0
      };
    }
    
    return { label: '', value: '', price_modifier: 0, lead_time_modifier: 0 };
  }
  
  /**
   * 価格モディファイアを文字列に変換
   * @param {Object|number} priceModifier - 価格モディファイア（オブジェクトまたは数値）
   * @returns {string} フォーマットされた文字列
   */
  formatPriceModifier(priceModifier) {
    if (priceModifier === null || priceModifier === undefined) return '';
    
    // 数値の場合（パースされた固定価格）
    if (typeof priceModifier === 'number') {
      if (priceModifier === 0) return '';
      return `+¥${priceModifier.toLocaleString()}`;
    }
    
    // オブジェクトの場合
    if (typeof priceModifier === 'object') {
      if (priceModifier.type === 'fixed') {
        const value = priceModifier.value || 0;
        if (value === 0) return '';
        return `+¥${value.toLocaleString()}`;
      }
      
      if (priceModifier.type === 'percentage') {
        const value = priceModifier.value || 0;
        return `×${(1 + value).toFixed(2)}`;
      }
      
      if (priceModifier.type === 'base_plus_unit') {
        const baseValue = priceModifier.value || priceModifier.base_price || 0;
        const unitPrice = priceModifier.unit_price || 0;
        return `基本¥${baseValue.toLocaleString()} + @¥${unitPrice}×数量`;
      }
    }
    
    return '';
  }
  
  /**
   * 納期モディファイアを文字列に変換
   * @param {Object|number} leadTimeModifier - 納期モディファイア（オブジェクトまたは数値）
   * @returns {string} フォーマットされた文字列
   */
  formatLeadTimeModifier(leadTimeModifier) {
    if (leadTimeModifier === null || leadTimeModifier === undefined) return '';
    
    // 商品の発送日計算方式に連動
    const calcType = window.productDeliveryCalcType || 'business_days';
    const daySuffix = calcType === 'calendar_days' ? '日' : '営業日';
    
    // 数値の場合（パースされた追加日数）
    if (typeof leadTimeModifier === 'number') {
      if (leadTimeModifier === 0) return '';
      return `+${leadTimeModifier}${daySuffix}`;
    }
    
    // オブジェクトの場合
    if (typeof leadTimeModifier === 'object') {
      if (leadTimeModifier.type === 'fixed') {
        return `納期: ${leadTimeModifier.value}${daySuffix}`;
      }
      
      if (leadTimeModifier.type === 'add_days') {
        if (leadTimeModifier.value === 0) return '';
        return `+${leadTimeModifier.value}${daySuffix}`;
      }
    }
    
    return '';
  }
  
  /**
   * 分納・納品代行の排他制御チェック
   * @param {Object} option - 選択しようとしているオプション
   * @param {Object} choice - 選択しようとしている選択肢
   * @returns {Object} {allowed: boolean, message: string}
   */
  checkExclusiveOptions(option, choice) {
    
    // 分納オプションのラベルパターン
    const isBunnoOption = (label) => {
      return label && (label.includes('分納') || label.includes('複数箇所'));
    };
    
    // 納品代行オプションのラベルパターン  
    const isNohinDaikoOption = (label) => {
      return label && (label.includes('納品代行') || label.includes('発送代行'));
    };
    
    // 現在選択しようとしているオプションのチェック
    const currentOptionLabel = option.label || '';
    const currentChoiceLabel = choice.label || '';
    
    // 「選択なし」「利用しない」等の場合は制限なし
    if (currentChoiceLabel.includes('選択なし') || 
        currentChoiceLabel.includes('利用しない') || 
        currentChoiceLabel.includes('なし')) {
      return { allowed: true, message: '' };
    }
    
    const isSelectingBunno = isBunnoOption(currentOptionLabel) || isBunnoOption(currentChoiceLabel);
    const isSelectingNohinDaiko = isNohinDaikoOption(currentOptionLabel) || isNohinDaikoOption(currentChoiceLabel);
    
    // 排他チェックが必要な場合
    if (isSelectingBunno || isSelectingNohinDaiko) {
      // 既に選択されている全てのオプションをチェック
      const allSelectedOptions = {
        ...this.selectedOptions.required,
        ...this.selectedOptions.optional
      };
      
      for (const [optionId, selectedChoice] of Object.entries(allSelectedOptions)) {
        if (!selectedChoice || optionId === option.id) continue;
        
        const selectedOption = this.optionsById[optionId];
        if (!selectedOption) continue;
        
        const selectedOptionLabel = selectedOption.label || '';
        const selectedChoiceLabel = selectedChoice.label || '';
        
        // 「選択なし」系は無視
        if (selectedChoiceLabel.includes('選択なし') || 
            selectedChoiceLabel.includes('利用しない') || 
            selectedChoiceLabel.includes('なし')) {
          continue;
        }
        
        const hasSelectedBunno = isBunnoOption(selectedOptionLabel) || isBunnoOption(selectedChoiceLabel);
        const hasSelectedNohinDaiko = isNohinDaikoOption(selectedOptionLabel) || isNohinDaikoOption(selectedChoiceLabel);
        
        // 分納を選択しようとしているが、既に納品代行が選択されている
        if (isSelectingBunno && hasSelectedNohinDaiko) {
          return {
            allowed: false,
            message: `「分納」オプションは「納品代行」オプションと同時に選択できません。\n\n` +
                    `現在選択中: ${selectedOptionLabel} - ${selectedChoiceLabel}\n\n` +
                    `どちらか一方のオプションのみご利用いただけます。`
          };
        }
        
        // 納品代行を選択しようとしているが、既に分納が選択されている
        if (isSelectingNohinDaiko && hasSelectedBunno) {
          return {
            allowed: false,
            message: `「納品代行」オプションは「分納」オプションと同時に選択できません。\n\n` +
                    `現在選択中: ${selectedOptionLabel} - ${selectedChoiceLabel}\n\n` +
                    `どちらか一方のオプションのみご利用いただけます。`
          };
        }
      }
    }
    
    return { allowed: true, message: '' };
  }
  
  /**
   * モーダルイベントの設定
   * @param {Object} option - オプションデータ
   */
  setupModalEvents(option) {
    const modal = document.getElementById('option-modal');
    const closeBtn = document.getElementById('option-modal-close');
    const cancelBtn = document.getElementById('option-modal-cancel');
    const applyBtn = document.getElementById('option-modal-apply');
    
    // サイズ入力フォームの表示/非表示制御
    const sizeInputContainer = document.getElementById(`size-input-${option.id}`);
    if (sizeInputContainer) {
      // すべてのラジオボタンに変更イベントを設定
      const radioButtons = modal.querySelectorAll(`input[name="option-choice-${option.id}"]`);
      radioButtons.forEach(radio => {
        radio.addEventListener('change', (e) => {
          const isCustomSize = e.target.getAttribute('data-is-custom-size') === 'true';
          if (isCustomSize) {
            sizeInputContainer.style.display = 'block';
          } else {
            sizeInputContainer.style.display = 'none';
          }
        });
      });
      
      // 初期状態を設定（すでに選択されているものがあれば）
      const checkedRadio = modal.querySelector(`input[name="option-choice-${option.id}"]:checked`);
      if (checkedRadio) {
        const isCustomSize = checkedRadio.getAttribute('data-is-custom-size') === 'true';
        if (isCustomSize) {
          sizeInputContainer.style.display = 'block';
        }
      }
    }
    
    // 閉じるボタン
    closeBtn.addEventListener('click', () => {
      modal.remove();
    });
    
    // キャンセルボタン
    cancelBtn.addEventListener('click', () => {
      modal.remove();
    });
    
    // 選択ボタン
    applyBtn.addEventListener('click', () => {
      this.applyOptionSelection(option);
      modal.remove();
    });
    
    // オーバーレイクリック
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }
  
  /**
   * オプション選択を適用
   * @param {Object} option - オプションデータ
   */
  applyOptionSelection(option) {
    const isRequired = option.required === true;
    
    // choices または options プロパティを取得
    const choices = option.choices || option.options || [];
    
    if (option.type === 'radio' || option.type === 'select') {
      const selected = document.querySelector(`input[name="option-choice-${option.id}"]:checked`);
      if (!selected) {
        console.warn('[ProductOptionsManager] No option selected');
        return;
      }
      
      // 元の選択肢を探す（valueで検索）
      const rawChoice = choices.find(c => {
        const normalized = this.normalizeChoice(c);
        return normalized.value === selected.value;
      });
      if (!rawChoice) {
        console.error('[ProductOptionsManager] Choice not found:', selected.value);
        return;
      }
      
      // 選択肢を正規化（price_modifier, lead_time_modifierを確実に含める）
      const choice = this.normalizeChoice(rawChoice);
      
      // 分納・納品代行の排他制御チェック
      const exclusiveCheck = this.checkExclusiveOptions(option, choice);
      if (!exclusiveCheck.allowed) {
        alert(exclusiveCheck.message);
        return;
      }
      
      // サイズ入力フォームの値を取得
      const isCustomSize = selected.getAttribute('data-is-custom-size') === 'true';
      let customSize = null;
      if (isCustomSize) {
        const widthInput = document.getElementById(`size-width-${option.id}`);
        const heightInput = document.getElementById(`size-height-${option.id}`);
        if (widthInput && heightInput) {
          const width = parseInt(widthInput.value) || 0;
          const height = parseInt(heightInput.value) || 0;
          if (width > 0 && height > 0) {
            customSize = { width, height };
          }
        }
      }
      
      // 選択内容にカスタムサイズを追加
      const selectedChoice = {
        ...choice,
        customSize: customSize
      };
      
      // 選択を保存（必須/任意に分けて管理）
      if (isRequired) {
        this.selectedOptions.required[option.id] = selectedChoice;
      } else {
        this.selectedOptions.optional[option.id] = selectedChoice;
      }
      
      // UIを更新（カスタムサイズの場合はサイズも表示）
      let displayLabel = choice.label;
      if (customSize) {
        displayLabel += ` (${customSize.width}mm × ${customSize.height}mm)`;
      }
      this.updateOptionDisplay(option.id, displayLabel);
      
    } else if (option.type === 'checkbox') {
      const checkboxes = document.querySelectorAll(`input[name="option-choice-${option.id}"]:checked`);
      const selectedChoices = Array.from(checkboxes).map(cb => {
        const rawChoice = choices.find(c => {
          const normalized = this.normalizeChoice(c);
          return normalized.value === cb.value;
        });
        return rawChoice ? this.normalizeChoice(rawChoice) : null;
      }).filter(c => c);
      
      // 選択を保存（必須/任意に分けて管理）
      if (selectedChoices.length > 0) {
        if (isRequired) {
          this.selectedOptions.required[option.id] = selectedChoices[0];
        } else {
          this.selectedOptions.optional[option.id] = selectedChoices[0];
        }
        this.updateOptionDisplay(option.id, selectedChoices[0].label);
      } else {
        // 選択解除
        if (isRequired) {
          delete this.selectedOptions.required[option.id];
        } else {
          delete this.selectedOptions.optional[option.id];
        }
        this.updateOptionDisplay(option.id, isRequired ? '選択してください' : '選択なし');
      }
    }
    
    
    // 価格を再計算
    this.recalculatePrice();
    
    // カートプロパティを更新
    this.updateCartProperties();
  }
  
  /**
   * オプション表示を更新
   * @param {string} optionId - オプションID
   * @param {string} displayText - 表示テキスト
   */
  updateOptionDisplay(optionId, displayText) {
    const displayElement = document.getElementById(`option-${optionId}-display`);
    if (displayElement) {
      displayElement.textContent = displayText;
    }
  }
  
  /**
   * 価格を再計算
   */
  recalculatePrice() {
    if (!this.priceCalculator) {
      console.warn('[ProductOptionsManager] Price calculator not available');
      return;
    }
    
    // 基本価格を取得（DynamicPriceCalculatorの現在の計算結果から）
    const currentResult = this.priceCalculator.calculatePrice();
    const basePrice = currentResult ? currentResult.basePrice : 0;
    
    // オプションによる価格調整を計算
    let optionPriceAdjustment = 0;
    let optionLeadTimeAdjustment = 0;
    
    // 必須オプションと任意オプションの両方を処理
    const allSelectedChoices = [
      ...Object.values(this.selectedOptions.required || {}),
      ...Object.values(this.selectedOptions.optional || {})
    ];
    
    allSelectedChoices.forEach(choice => {
      if (choice && choice.price_modifier) {
        optionPriceAdjustment += this.calculatePriceAdjustment(choice.price_modifier, basePrice);
      }
      if (choice && choice.lead_time_modifier) {
        optionLeadTimeAdjustment += this.calculateLeadTimeAdjustment(choice.lead_time_modifier);
      }
    });
    
    
    // 調整値を保存（他のコンポーネントから参照できるように）
    this.lastCalculatedAdjustments = {
      price: optionPriceAdjustment,
      leadTime: optionLeadTimeAdjustment
    };
    
    // 価格計算システムに調整値を通知
    if (this.priceCalculator && this.priceCalculator.applyOptionAdjustments) {
      this.priceCalculator.applyOptionAdjustments(optionPriceAdjustment, optionLeadTimeAdjustment);
    } else {
      console.warn('[ProductOptionsManager] Price calculator does not support option adjustments');
    }
  }
  
  /**
   * オプション調整値を取得
   * 従量制オプションがある場合は現在の部数で再計算
   * @returns {Object} {price: number, leadTime: number}
   */
  getOptionAdjustments() {
    // 従量制オプションがある可能性があるので、毎回再計算する
    let optionPriceAdjustment = 0;
    let optionLeadTimeAdjustment = 0;
    
    // selectedOptionsが初期化されていない場合はデフォルト値を返す
    if (!this.selectedOptions || !this.selectedOptions.required || !this.selectedOptions.optional) {
      return { price: 0, leadTime: 0 };
    }
    
    // 選択されているオプションを処理
    const allSelections = {
      ...this.selectedOptions.required,
      ...this.selectedOptions.optional
    };
    
    // デバッグ: 選択されているオプションの詳細をログ出力
    
    Object.entries(allSelections).forEach(([optionId, choice]) => {
      if (!choice) return;
      
      // デバッグ: 各選択肢の詳細
      
      if (choice.price_modifier) {
        const priceAdj = this.calculatePriceAdjustment(choice.price_modifier, 0);
        optionPriceAdjustment += priceAdj;
      }
      if (choice.lead_time_modifier) {
        const leadAdj = this.calculateLeadTimeAdjustment(choice.lead_time_modifier);
        optionLeadTimeAdjustment += leadAdj;
      }
    });
    
    
    return { price: optionPriceAdjustment, leadTime: optionLeadTimeAdjustment };
  }
  
  /**
   * 価格調整を計算
   * @param {Object|number} priceModifier - 価格モディファイア（オブジェクトまたは数値）
   * @param {number} basePrice - 基本価格
   * @returns {number} 調整額
   */
  calculatePriceAdjustment(priceModifier, basePrice) {
    if (!priceModifier && priceModifier !== 0) {
      return 0;
    }
    
    // 数値が直接渡された場合（Metaobjectからの固定値）
    if (typeof priceModifier === 'number') {
      return priceModifier;
    }
    
    // オブジェクトの場合（複数フォーマットに対応）
    if (typeof priceModifier === 'object') {
      // 固定制
      if (priceModifier.type === 'fixed') {
        // amount または value キーに対応
        const value = parseFloat(priceModifier.amount || priceModifier.value);
        return isNaN(value) ? 0 : value;
      }
      
      // パーセンテージ
      if (priceModifier.type === 'percentage') {
        const value = parseFloat(priceModifier.value);
        return isNaN(value) ? 0 : basePrice * value;
      }
      
      // 従量制（base_plus_unit）
      if (priceModifier.type === 'base_plus_unit') {
        const quantity = this.priceCalculator ? this.priceCalculator.currentSelection.quantity : 100;
        // base_amount または value キーに対応
        const baseValue = parseFloat(priceModifier.base_amount || priceModifier.value || priceModifier.base_price || 0);
        const unitPrice = parseFloat(priceModifier.unit_price || 0);
        
        
        if (isNaN(baseValue) || isNaN(unitPrice)) {
          return 0;
        }
        
        return baseValue + (unitPrice * quantity);
      }
    }
    
    return 0;
  }
  
  /**
   * 納期調整を計算
   * @param {Object|number} leadTimeModifier - 納期モディファイア（オブジェクトまたは数値）
   * @returns {number} 調整日数
   */
  calculateLeadTimeAdjustment(leadTimeModifier) {
    if (!leadTimeModifier && leadTimeModifier !== 0) {
      return 0;
    }
    
    // 数値が直接渡された場合（Metaobjectからの固定値）
    if (typeof leadTimeModifier === 'number') {
      return leadTimeModifier;
    }
    
    // オブジェクトの場合（複数フォーマットに対応）
    if (typeof leadTimeModifier === 'object') {
      // type: 'days' 形式（CSVインポート時の形式）
      if (leadTimeModifier.type === 'days') {
        const value = parseFloat(leadTimeModifier.amount || leadTimeModifier.value);
        return isNaN(value) ? 0 : value;
      }
      
      // type: 'fixed' 形式
      if (leadTimeModifier.type === 'fixed') {
        const value = parseFloat(leadTimeModifier.amount || leadTimeModifier.value);
        return isNaN(value) ? 0 : value;
      }
      
      // type: 'add_days' 形式
      if (leadTimeModifier.type === 'add_days') {
        const value = parseFloat(leadTimeModifier.amount || leadTimeModifier.value);
        return isNaN(value) ? 0 : value;
      }
      
      // type がない場合、amount または value キーを探す
      if (leadTimeModifier.amount !== undefined) {
        const value = parseFloat(leadTimeModifier.amount);
        return isNaN(value) ? 0 : value;
      }
      if (leadTimeModifier.value !== undefined) {
        const value = parseFloat(leadTimeModifier.value);
        return isNaN(value) ? 0 : value;
      }
    }
    
    return 0;
  }
  
  /**
   * カートプロパティを更新
   */
  updateCartProperties() {
    const requiredOptionsContainer = document.getElementById('cart-required-options');
    const optionalOptionsContainer = document.getElementById('cart-optional-options');
    
    if (!requiredOptionsContainer || !optionalOptionsContainer) {
      console.warn('[ProductOptionsManager] Cart property containers not found');
      return;
    }
    
    // 既存のプロパティをクリア
    requiredOptionsContainer.innerHTML = '';
    optionalOptionsContainer.innerHTML = '';
    
    // オプションと追加フィールドをカートプロパティに追加するヘルパー関数
    const addOptionToCart = (container, option, choice) => {
      
      // メインのオプション値
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${option.label}]`;
      input.value = choice.label;
      container.appendChild(input);
      
      // 追加入力フィールドの値があれば追加
      if (choice.additionalFields && typeof choice.additionalFields === 'object') {
        Object.entries(choice.additionalFields).forEach(([fieldId, fieldValue]) => {
          if (fieldValue && fieldValue.trim() !== '') {
            // フィールドのラベルを取得（オプション定義から）
            const fieldDef = option.additionalFields?.find(f => f.id === fieldId);
            const fieldLabel = fieldDef ? fieldDef.label : fieldId;
            
            
            const additionalInput = document.createElement('input');
            additionalInput.type = 'hidden';
            additionalInput.name = `properties[${option.label}_${fieldLabel}]`;
            additionalInput.value = fieldValue;
            container.appendChild(additionalInput);
          }
        });
      }
    };
    
    // 必須オプションを処理
    Object.entries(this.selectedOptions.required || {}).forEach(([optionId, choice]) => {
      if (choice) {
        const option = this.optionsById[optionId];
        if (!option) return;
        addOptionToCart(requiredOptionsContainer, option, choice);
      }
    });
    
    // 任意オプションを処理
    Object.entries(this.selectedOptions.optional || {}).forEach(([optionId, choice]) => {
      if (choice) {
        const option = this.optionsById[optionId];
        if (!option) return;
        addOptionToCart(optionalOptionsContainer, option, choice);
      }
    });
    
  }
  
  /**
   * 必須オプションがすべて選択されているかチェック
   * @returns {boolean} すべて選択済みの場合true
   */
  areRequiredOptionsSelected() {
    // 重複を排除したユニークな必須オプションをチェック
    const uniqueRequired = Object.values(this.optionsById).filter(opt => opt.required === true);
    return uniqueRequired.every(option => {
      return this.selectedOptions.required && this.selectedOptions.required[option.id] != null;
    });
  }
  
  /**
   * オプションが表示されているかどうかをチェック（ルールベース）
   * GroupedOptionsVisibilityモジュールを使用して、印刷色数に応じた表示/非表示を判定
   * @param {Object} option - オプションオブジェクト
   * @returns {boolean} 表示されている場合はtrue
   */
  isOptionVisibleByRule(option) {
    // GroupedOptionsVisibilityが利用可能な場合はそれを使用
    if (window.GroupedOptionsVisibility && window.GroupedOptionsVisibility.isOptionVisible) {
      const printColor = window.GroupedOptionsVisibility.getCurrentPrintColor?.() || '';
      const rules = this.conditionalVisibilityRules || [];
      const rule = window.GroupedOptionsVisibility.getColorVisibilityRule?.(printColor, rules) || null;
      
      return window.GroupedOptionsVisibility.isOptionVisible(option, rule);
    }
    
    // フォールバック: 自前のルールでチェック
    const printColor = this.priceCalculator?.currentSelection?.printColor || '';
    if (!printColor) return true;
    if (!Array.isArray(this.conditionalVisibilityRules)) return true;

    for (const rule of this.conditionalVisibilityRules) {
      if (rule.triggerPattern.test(printColor)) {
        const optionLabel = option.label || '';
        // 非表示リストに含まれる場合
        if (rule.hideOptions.some(pattern => optionLabel.includes(pattern))) {
          return false;
        }
        break;
      }
    }
    return true;
  }
  
  /**
   * 未選択の必須オプションを取得
   * 注意: 非表示のオプションは除外される（印刷色数に応じた条件付き表示に対応）
   * @returns {Array} 未選択の必須オプションの配列 [{id, label}, ...]
   */
  getUnselectedRequiredOptions() {
    const unselected = [];
    // 重複を排除したユニークな必須オプションをチェック
    const uniqueRequired = Object.values(this.optionsById).filter(opt => opt.required === true);
    
    uniqueRequired.forEach(option => {
      // ルールベースで非表示のオプションはスキップ（印刷色数に応じて表示/非表示が切り替わるため）
      if (!this.isOptionVisibleByRule(option)) {
        return;
      }
      
      if (!this.selectedOptions.required || !this.selectedOptions.required[option.id]) {
        unselected.push({
          id: option.id,
          label: option.label
        });
      }
    });
    
    return unselected;
  }
  
  /**
   * 必須オプションのバリデーション
   * @returns {Object} {isValid: boolean, unselected: Array, message: string}
   */
  validateRequiredOptions() {
    const unselected = this.getUnselectedRequiredOptions();
    const isValid = unselected.length === 0;
    
    let message = '';
    if (!isValid) {
      const count = unselected.length;
      message = `⚠️ 必須オプションが未選択です\n\n`;
      message += `以下の${count}件のオプションを選択してください:\n\n`;
      message += unselected.map((opt, index) => `${index + 1}. ${opt.label}`).join('\n');
      message += `\n\nサイドバーの各オプションボタンをクリックして選択してください。`;
      
      // 未選択の必須オプションをハイライト表示
      this.highlightUnselectedRequiredOptions(unselected);
    }
    
    return {
      isValid,
      unselected,
      message
    };
  }
  
  /**
   * 未選択の必須オプションをハイライト表示
   * @param {Array} unselectedOptions - 未選択のオプション配列
   */
  highlightUnselectedRequiredOptions(unselectedOptions) {
    // 全てのオプションボタンのハイライトをクリア
    document.querySelectorAll('.option-button').forEach(button => {
      button.style.border = '';
      button.style.background = '';
      button.style.animation = '';
    });
    
    // 未選択の必須オプションをハイライト
    unselectedOptions.forEach(opt => {
      const button = document.querySelector(`.option-button[data-option-id="${opt.id}"]`);
      if (button) {
        button.style.border = '2px solid #dc3545';
        button.style.background = '#ffe6e6';
        button.style.animation = 'pulse 1s ease-in-out 3';
      }
    });
    
    // パルスアニメーションのスタイルを追加（まだ存在しない場合）
    if (!document.getElementById('option-pulse-animation')) {
      const style = document.createElement('style');
      style.id = 'option-pulse-animation';
      style.textContent = `
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  /**
   * 選択されたオプションのサマリーを取得
   * @returns {Object} サマリーオブジェクト
   */
  getSelectionSummary() {
    const required = {};
    const optional = {};
    
    Object.entries(this.selectedOptions).forEach(([optionId, choice]) => {
      if (choice) {
        const option = this.optionsById[optionId];
        if (!option) return;
        
        // requiredプロパティで判別
        if (option.required) {
          required[option.label] = choice.label;
        } else {
          optional[option.label] = choice.label;
        }
      }
    });
    
    return { required, optional };
  }
}

// グローバルに公開
window.ProductOptionsManager = ProductOptionsManager;

